import React, { useMemo, useState } from 'react';
import ResponsavelTransparencia from '../components/ResponsavelTransparencia';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  transparenciaService,
  type EmendaPublicaDTO,
  type TransparenciaFiltros,
} from '../services/transparenciaService';
import { emendaClassificationLabel, emendaNumeroExercicio } from '../utils/emendaFormatters';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatEnum,
  formatFuncaoGoverno,
  formatPercent,
  numeroComAno,
  rotuloInstrumento,
  rotuloProcesso,
  usePortalTitle,
} from '../utils/transparenciaFormat';
import DashboardPagination from '../components/DashboardPagination';
import { PortalAcessibilidadeShell, SituacaoBadge, usePortalAcessibilidade } from '../components/PortalAcessibilidade';

const ANO_ATUAL = new Date().getFullYear();
/** Valor do filtro de exercício que consulta todos os anos (o backend trata ausência como "todos"). */
const TODOS_EXERCICIOS = 'todos';
const ESFERAS = ['Todos', 'Municipal', 'Estadual', 'Federal'];
const STATUS_OPTIONS = ['Todos', 'Recebido', 'Iniciado', 'Em execução', 'Concluído', 'Devolvido'];
const EXECUCAO_OPTIONS = [
  { value: 'Todos', label: 'Todas' },
  { value: 'EMPENHADO', label: 'Com empenho' },
  { value: 'SEM_EMPENHO', label: 'Sem empenho' },
  { value: 'PAGO', label: 'Com pagamento' },
  { value: 'SEM_PAGAMENTO', label: 'Sem pagamento' },
];
const POR_PAGINA_OPTIONS = [10, 20, 50, 100];

type Exercicio = number | typeof TODOS_EXERCICIOS;

const readUrlState = (params: URLSearchParams) => {
  const exercicioBruto = params.get('exercicio');
  const exercicioParam = Number(exercicioBruto);
  const paginaParam = Number(params.get('pagina'));
  const porPaginaParam = Number(params.get('porPagina'));
  const esferaParam = params.get('esfera') || 'Todos';
  const statusParam = params.get('status') || 'Todos';
  const execucaoParam = params.get('execucao') || 'Todos';
  const exercicio: Exercicio = exercicioBruto === TODOS_EXERCICIOS
    ? TODOS_EXERCICIOS
    : Number.isInteger(exercicioParam) && exercicioParam > 0 ? exercicioParam : ANO_ATUAL;
  return {
    exercicio,
    esfera: ESFERAS.includes(esferaParam) ? esferaParam : 'Todos',
    tipo: params.get('tipo') || 'Todos',
    status: STATUS_OPTIONS.includes(statusParam) ? statusParam : 'Todos',
    execucao: EXECUCAO_OPTIONS.some(item => item.value === execucaoParam) ? execucaoParam : 'Todos',
    autor: params.get('autor') || '',
    instituicao: params.get('instituicao') || '',
    objeto: params.get('objeto') || '',
    pagina: Number.isInteger(paginaParam) && paginaParam > 0 ? paginaParam : 1,
    porPagina: POR_PAGINA_OPTIONS.includes(porPaginaParam) ? porPaginaParam : 20,
  };
};

type Formulario = Omit<ReturnType<typeof readUrlState>, 'pagina' | 'porPagina'>;

const paraFiltros = (form: Formulario): TransparenciaFiltros => ({
  exercicio: form.exercicio === TODOS_EXERCICIOS ? undefined : form.exercicio,
  esfera: form.esfera === 'Todos' ? undefined : form.esfera,
  tipo: form.tipo === 'Todos' ? undefined : form.tipo,
  status: form.status === 'Todos' ? undefined : form.status,
  execucao: form.execucao === 'Todos' ? undefined : form.execucao,
  autor: form.autor.trim() || undefined,
  instituicao: form.instituicao.trim() || undefined,
  objeto: form.objeto.trim() || undefined,
});

const FORM_PADRAO: Formulario = {
  exercicio: ANO_ATUAL, esfera: 'Todos', tipo: 'Todos', status: 'Todos', execucao: 'Todos', autor: '', instituicao: '', objeto: '',
};

const TransparenciaPublicaPage: React.FC = () => {
  usePortalTitle('Emendas parlamentares');
  const { tema, barra } = usePortalAcessibilidade();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlState = readUrlState(searchParams);
  const [form, setForm] = useState<Formulario>(initialUrlState);
  const [filtrosAtivos, setFiltrosAtivos] = useState<TransparenciaFiltros>(paraFiltros(initialUrlState));
  const [exercicioAtivo, setExercicioAtivo] = useState<Exercicio>(initialUrlState.exercicio);
  const [pagina, setPagina] = useState(initialUrlState.pagina);
  const [porPagina, setPorPagina] = useState(initialUrlState.porPagina);
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [emendaAberta, setEmendaAberta] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['transparencia-emendas', filtrosAtivos, pagina, porPagina],
    queryFn: () => transparenciaService.list(filtrosAtivos, pagina - 1, porPagina),
    staleTime: 5 * 60 * 1000,
  });
  const { data: tiposEmenda = [] } = useQuery({
    queryKey: ['transparencia-emendas-tipos'],
    queryFn: transparenciaService.tipos,
    staleTime: 30 * 60 * 1000,
  });
  const nomeTipo = (codigo?: string): string =>
    tiposEmenda.find(item => item.codigo === codigo)?.nome ?? emendaClassificationLabel(codigo);
  const { data: metadata } = useQuery({
    queryKey: ['transparencia-emendas-metadados'],
    queryFn: transparenciaService.metadata,
    staleTime: 5 * 60 * 1000,
  });
  const { data: totaisData } = useQuery({
    queryKey: ['transparencia-emendas-totais', filtrosAtivos],
    queryFn: () => transparenciaService.totais(filtrosAtivos),
    staleTime: 5 * 60 * 1000,
  });

  const emendas = useMemo<EmendaPublicaDTO[]>(() => data?.items ?? [], [data]);
  const totais = totaisData ?? { previsto: 0, empenhado: 0, pago: 0 };
  const totalItems = data?.totalItems ?? 0;

  // Anos oferecidos: o período que o próprio portal publica, mais o ano corrente e o da URL.
  const anos = useMemo(() => {
    const conjunto = new Set<number>([ANO_ATUAL]);
    if (metadata?.primeiroExercicio && metadata?.ultimoExercicio) {
      for (let ano = metadata.primeiroExercicio; ano <= metadata.ultimoExercicio; ano += 1) conjunto.add(ano);
    }
    if (typeof form.exercicio === 'number') conjunto.add(form.exercicio);
    return [...conjunto].sort((a, b) => b - a);
  }, [metadata, form.exercicio]);

  const atualizar = <K extends keyof Formulario>(campo: K, valor: Formulario[K]) =>
    setForm(atual => ({ ...atual, [campo]: valor }));

  const syncUrl = (filtros: TransparenciaFiltros, exercicio: Exercicio, nextPage: number, nextPageSize: number) => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([key, value]) => {
      if (value != null && value !== '') params.set(key, String(value));
    });
    if (exercicio === TODOS_EXERCICIOS) params.set('exercicio', TODOS_EXERCICIOS);
    if (nextPage > 1) params.set('pagina', String(nextPage));
    if (nextPageSize !== 20) params.set('porPagina', String(nextPageSize));
    setSearchParams(params, { replace: true });
  };

  const aplicar = (novo: Formulario) => {
    const filtros = paraFiltros(novo);
    setPagina(1);
    setFiltrosAtivos(filtros);
    setExercicioAtivo(novo.exercicio);
    setEmendaAberta(null);
    syncUrl(filtros, novo.exercicio, 1, porPagina);
  };

  const handleBuscar = () => aplicar(form);
  const handleLimpar = () => { setForm(FORM_PADRAO); aplicar(FORM_PADRAO); };
  const enterBusca = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleBuscar(); };

  // Filtros em uso, em texto, para o estado vazio dizer o que zerou o resultado.
  const descricaoFiltros = [
    exercicioAtivo === TODOS_EXERCICIOS ? 'todos os exercícios' : `exercício ${exercicioAtivo}`,
    filtrosAtivos.esfera && `origem ${filtrosAtivos.esfera}`,
    filtrosAtivos.tipo && `tipo ${nomeTipo(filtrosAtivos.tipo)}`,
    filtrosAtivos.status && `situação ${filtrosAtivos.status}`,
    filtrosAtivos.execucao && EXECUCAO_OPTIONS.find(item => item.value === filtrosAtivos.execucao)?.label.toLocaleLowerCase('pt-BR'),
    filtrosAtivos.autor && `parlamentar “${filtrosAtivos.autor}”`,
    filtrosAtivos.instituicao && `beneficiário “${filtrosAtivos.instituicao}”`,
    filtrosAtivos.objeto && `objeto “${filtrosAtivos.objeto}”`,
  ].filter(Boolean) as string[];

  const percentualPago = formatPercent(totais.pago, totais.previsto);
  const labelClass = 'text-sm font-medium';
  const exportFiltros = filtrosAtivos;

  const detalheExpandido = (emenda: EmendaPublicaDTO) => {
    const movimentos = emenda.execucaoFinanceira ?? [];
    return (
      <div className="space-y-5">
        <section className={`rounded-lg p-4 ${tema.card}`}>
          <h3 className="text-sm font-bold">Identificação e repasse</h3>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm lg:grid-cols-5">
            {[
              ['Forma de repasse', emenda.tipoTransferencia],
              ['Instrumento', rotuloInstrumento(emenda)],
              ['Processo de compra', rotuloProcesso(emenda)],
              ['Início previsto', formatDate(emenda.periodoInicio)],
              ['Término previsto', formatDate(emenda.periodoFim)],
            ].map(([label, value]) => <div key={label}><dt className={`text-xs font-semibold uppercase ${tema.label}`}>{label}</dt><dd className="mt-0.5 font-medium">{value || 'Não informado'}</dd></div>)}
          </dl>
          <div className={`mt-4 border-t pt-4 ${tema.divider}`}><p className={`text-xs font-semibold uppercase ${tema.label}`}>Objeto / finalidade</p><p className="mt-1 leading-6">{emenda.objectDetail || 'Não informado'}</p></div>
        </section>
        {(emenda.contratos?.length ?? 0) > 0 && (
          <section>
            <h3 className="text-sm font-bold">Contratos e Termo de Fomento</h3>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              {emenda.contratos!.map((contrato, contratoIndex) => (
                <article key={`${contrato.numero}-${contrato.ano}-${contratoIndex}`} className={`rounded-lg p-3 ${tema.card}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="font-semibold">{formatEnum(contrato.instrumento)} {numeroComAno(contrato.numero, contrato.ano)}</p>
                    <p className="whitespace-nowrap font-bold">{formatCurrency(contrato.valor)}</p>
                  </div>
                  <p className={`mt-1 text-xs ${tema.muted}`}>Vigência: {formatDate(contrato.vigenciaInicio)} a {formatDate(contrato.vigenciaFim)}</p>
                  {contrato.processoCompraNumero && <p className={`mt-0.5 text-xs ${tema.muted}`}>Processo de compra: {numeroComAno(contrato.processoCompraNumero, contrato.processoCompraAno)}</p>}
                  <p className="mt-2 break-words text-xs leading-5">{contrato.objeto || 'Objeto não informado'}</p>
                </article>
              ))}
            </div>
          </section>
        )}
        {(emenda.atosLegais?.length ?? 0) > 0 && (
          <section>
            <h3 className="text-sm font-bold">Atos legais da dotação</h3>
            <ul className="mt-3 space-y-2">
              {emenda.atosLegais!.map((ato, atoIndex) => (
                <li key={`${ato.numero}-${ato.ano}-${atoIndex}`} className={`rounded-lg p-3 text-sm ${tema.card}`}>
                  <p className="font-semibold">Ato legal nº {numeroComAno(ato.numero, ato.ano)}{ato.dataRevogacao ? ` (revogado em ${formatDate(ato.dataRevogacao)})` : ''}</p>
                  {ato.ementa && <p className="mt-1 text-xs leading-5">{ato.ementa}</p>}
                  <p className={`mt-1 text-xs ${tema.muted}`}>Publicado em {formatDate(ato.dataPublicacao)}{ato.linkOficial && <> · <a href={ato.linkOficial} target="_blank" rel="noopener noreferrer" className="font-semibold underline">Texto oficial</a></>}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section>
          <h3 className="text-sm font-bold">Execução orçamentária e financeira</h3>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[['Empenhado', emenda.valorEmpenhado], ['Liquidado', emenda.valorLiquidado], ['Pago', emenda.valorPago]].map(([label, value]) => <div key={String(label)} className={`rounded-lg p-3 ${tema.card}`}><p className={`text-xs ${tema.muted}`}>{label}</p><p className="mt-1 font-bold">{formatCurrency(value as number | undefined)}</p></div>)}
          </div>
          {movimentos.length === 0 ? <p className={`mt-4 rounded-lg border border-dashed p-4 text-sm ${tema.divider} ${tema.muted}`}>Não houve execução orçamentária e financeira registrada para esta emenda.</p> : (
            <div className="mt-4 grid gap-3 lg:grid-cols-3">
              {movimentos.map((movimento, movimentoIndex) => (
                <article key={`${movimento.numeroDocumento}-${movimentoIndex}`} className={`rounded-lg p-3 ${tema.card}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{formatEnum(movimento.tipo)}{movimento.natureza === 'ANULACAO' ? ' (anulação)' : ''}</p>
                      <p className={`mt-0.5 text-xs ${tema.muted}`}>{movimento.numeroDocumento || 'Documento não informado'} · {formatDate(movimento.dataMovimento)}</p>
                    </div>
                    <p className="whitespace-nowrap font-bold">{formatCurrency(movimento.valor)}</p>
                  </div>
                  <div className="mt-3 grid gap-3 text-xs">
                    <div><p className={`font-semibold uppercase ${tema.label}`}>Credor</p><p className="mt-1 break-words">{movimento.credor || emenda.institutionName || 'Não informado'}</p></div>
                    <div><p className={`font-semibold uppercase ${tema.label}`}>Descrição da despesa</p><p className="mt-1 break-words leading-5">{movimento.descricaoDespesa || emenda.objectDetail || 'Não informado'}</p></div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    );
  };

  const linkEmenda = (emenda: EmendaPublicaDTO) => emenda.officialCode
    ? `/transparencia/emendas/${encodeURIComponent(emenda.officialCode)}`
    : null;

  return (
    <PortalAcessibilidadeShell tema={tema} barra={barra}>
      <header className={tema.header}>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-6 lg:px-8">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${tema.headerKicker}`}>Portal da Transparência</p>
            <h1 className="mt-1 text-2xl font-bold">Emendas parlamentares</h1>
            <p className={`mt-1 text-sm ${tema.headerText}`}>
              Quanto foi indicado, para quem, por qual parlamentar e em que situação está
            </p>
          </div>
          <nav aria-label="Outras consultas" className="flex flex-wrap items-center gap-2">
            <Link to="/transparencia/parlamentares" className={`rounded-md border px-4 py-2 text-sm font-semibold ${tema.headerButton}`}>
              Consultar por parlamentar
            </Link>
            <Link to="/login" className={`rounded-md border px-4 py-2 text-sm font-semibold ${tema.headerButton}`}>
              Acessar o sistema
            </Link>
          </nav>
        </div>
      </header>

      <main id="conteudo-principal" className="mx-auto w-full max-w-7xl flex-1 space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        <section className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-xl px-5 py-3 text-sm ${tema.card}`} aria-label="Atualização dos dados">
          <p><span className="font-semibold">Período coberto:</span> {metadata?.primeiroExercicio ?? '—'} a {metadata?.ultimoExercicio ?? '—'}</p>
          <p><span className="font-semibold">Última atualização:</span> {formatDateTime(metadata?.ultimaAtualizacao)}</p>
          <p><span className="font-semibold">No portal:</span> {(metadata?.totalRegistros ?? 0).toLocaleString('pt-BR')} emendas, todos os exercícios</p>
          {metadata?.responsavel && <ResponsavelTransparencia responsavel={metadata.responsavel} />}
        </section>

        <section className={`overflow-hidden rounded-xl ${tema.card}`} aria-label="Resumo do resultado">
          <p className={`border-b px-4 py-2 text-xs font-semibold ${tema.divider} ${tema.muted}`}>
            Resumo do resultado · {exercicioAtivo === TODOS_EXERCICIOS ? 'todos os exercícios' : `exercício ${exercicioAtivo}`}
          </p>
          <dl className={`grid grid-cols-2 divide-x divide-y lg:grid-cols-4 lg:divide-y-0 ${tema.divider}`}>
            {[
              ['Emendas', totalItems.toLocaleString('pt-BR'), null],
              ['Previsto', formatCurrency(totais.previsto), null],
              ['Empenhado', formatCurrency(totais.empenhado), formatPercent(totais.empenhado, totais.previsto)],
              ['Pago', formatCurrency(totais.pago), percentualPago],
            ].map(([label, value, extra]) => (
              <div key={label} className="px-4 py-3">
                <dt className={`text-xs font-semibold uppercase tracking-wide ${tema.label}`}>{label}</dt>
                <dd className="mt-1 text-base font-bold">{value}</dd>
                {extra && <dd className={`text-xs ${tema.muted}`}>{extra} do previsto</dd>}
              </div>
            ))}
          </dl>
        </section>

        <section className={`rounded-xl ${tema.card}`}>
          <button type="button" className="flex w-full items-center justify-between px-5 py-4 text-left" onClick={() => setFiltrosAbertos(value => !value)} aria-expanded={filtrosAbertos} aria-controls="filtros-pesquisa">
            <span className="flex items-center gap-2 text-sm font-semibold"><FunnelIcon className="h-4 w-4" aria-hidden="true" />Filtros de pesquisa</span>
            {filtrosAbertos ? <ChevronUpIcon className="h-4 w-4" aria-hidden="true" /> : <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />}
          </button>
          {filtrosAbertos && (
            <div id="filtros-pesquisa" className={`border-t px-5 pb-5 pt-4 ${tema.divider}`}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <label className={labelClass}>Exercício
                  <select value={String(form.exercicio)} onChange={e => atualizar('exercicio', e.target.value === TODOS_EXERCICIOS ? TODOS_EXERCICIOS : Number(e.target.value))} className={`mt-1 ${tema.input}`}>
                    <option value={TODOS_EXERCICIOS}>Todos os exercícios</option>
                    {anos.map(ano => <option key={ano} value={ano}>{ano}</option>)}
                  </select>
                </label>
                <label className={labelClass}>Parlamentar
                  <input value={form.autor} onChange={e => atualizar('autor', e.target.value)} onKeyDown={enterBusca} placeholder="Nome do parlamentar" className={`mt-1 ${tema.input}`} />
                </label>
                <label className={labelClass}>Beneficiário
                  <input value={form.instituicao} onChange={e => atualizar('instituicao', e.target.value)} onKeyDown={enterBusca} placeholder="Nome da instituição" className={`mt-1 ${tema.input}`} />
                </label>
                <label className={labelClass}>Objeto
                  <input value={form.objeto} onChange={e => atualizar('objeto', e.target.value)} onKeyDown={enterBusca} placeholder="Palavra do objeto" className={`mt-1 ${tema.input}`} />
                </label>
                <label className={labelClass}>Situação
                  <select value={form.status} onChange={e => atualizar('status', e.target.value)} className={`mt-1 ${tema.input}`}>{STATUS_OPTIONS.map(item => <option key={item}>{item}</option>)}</select>
                </label>
                <label className={labelClass}>Execução financeira
                  <select value={form.execucao} onChange={e => atualizar('execucao', e.target.value)} className={`mt-1 ${tema.input}`}>{EXECUCAO_OPTIONS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
                </label>
                <label className={labelClass}>Tipo de emenda
                  <select value={form.tipo} onChange={e => atualizar('tipo', e.target.value)} className={`mt-1 ${tema.input}`}><option value="Todos">Todos</option>{tiposEmenda.map(item => <option key={item.codigo} value={item.codigo}>{item.nome}</option>)}</select>
                </label>
                <label className={labelClass}>Origem
                  <select value={form.esfera} onChange={e => atualizar('esfera', e.target.value)} className={`mt-1 ${tema.input}`}>{ESFERAS.map(item => <option key={item}>{item}</option>)}</select>
                </label>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" onClick={handleBuscar} className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ${tema.primary}`}><MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />Buscar</button>
                <button type="button" onClick={handleLimpar} className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold ${tema.secondary}`}><XMarkIcon className="h-4 w-4" aria-hidden="true" />Limpar</button>
                <span className="hidden flex-1 sm:block" />
                <a href={transparenciaService.exportUrl('csv', exportFiltros)} download className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold ${tema.secondary}`}><ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />Baixar CSV</a>
                <a href={transparenciaService.exportUrl('json', exportFiltros)} download className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold ${tema.secondary}`}><ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />Baixar JSON</a>
              </div>
              <p className={`mt-2 text-xs ${tema.muted}`}>Os arquivos trazem o resultado dos filtros aplicados.</p>
            </div>
          )}
        </section>

        <section className={`overflow-hidden rounded-xl ${tema.card}`} aria-labelledby="titulo-resultado">
          <div className={`border-b px-5 py-4 ${tema.divider}`}>
            <h2 id="titulo-resultado" className="font-semibold">Resultado da pesquisa</h2>
            <p className={`mt-0.5 text-xs ${tema.muted}`}>Abra uma emenda para ver plano de trabalho, execução financeira e prestação de contas.</p>
          </div>

          {isLoading && <div className="py-16 text-center text-sm" role="status">Carregando dados...</div>}
          {isError && <div className="px-5 py-12 text-center"><p className="text-sm text-red-600 dark:text-red-400">Não foi possível carregar os dados.</p><button type="button" onClick={() => refetch()} className={`mt-3 rounded-md border px-3 py-2 text-sm ${tema.secondary}`}>Tentar novamente</button></div>}
          {!isLoading && !isError && emendas.length === 0 && (
            <div className="px-5 py-14 text-center" role="status">
              <p className="font-semibold">Nenhuma emenda encontrada.</p>
              <p className={`mx-auto mt-1 max-w-xl text-sm ${tema.muted}`}>Filtros aplicados: {descricaoFiltros.join(' · ')}.</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {exercicioAtivo !== TODOS_EXERCICIOS && (
                  <button type="button" onClick={() => { const novo = { ...form, exercicio: TODOS_EXERCICIOS as Exercicio }; setForm(novo); aplicar(novo); }} className={`rounded-md border px-3 py-2 text-sm font-semibold ${tema.secondary}`}>
                    Buscar em todos os exercícios
                  </button>
                )}
                <button type="button" onClick={handleLimpar} className={`rounded-md px-3 py-2 text-sm font-semibold ${tema.primary}`}>Limpar filtros</button>
              </div>
            </div>
          )}

          {!isLoading && !isError && emendas.length > 0 && (
            <>
              {/* Celular: cartões. A tabela de 8 colunas escondia valor e situação fora da tela. */}
              <ul className={`divide-y lg:hidden ${tema.divider}`}>
                {emendas.map((emenda, index) => {
                  const identificacao = emendaNumeroExercicio(emenda.numeroEmenda, emenda.exercicio, emenda.officialCode);
                  const destino = linkEmenda(emenda);
                  return (
                    <li key={emenda.id ?? index} className="px-4 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-semibold">Emenda {identificacao.numero}/{identificacao.exercicio}</p>
                        <SituacaoBadge status={emenda.status} alto={tema.alto} />
                      </div>
                      <p className="mt-1 text-sm font-medium">{emenda.councilorName || 'Autoria não informada'}</p>
                      <p className={`text-xs ${tema.muted}`}>Para: {emenda.institutionName || 'beneficiário não informado'}</p>
                      <p className="mt-2 line-clamp-2 text-sm">{emenda.objectDetail || 'Objeto não informado'}</p>
                      <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div><dt className={`text-xs ${tema.label}`}>Previsto</dt><dd className="font-semibold">{formatCurrency(emenda.value)}</dd></div>
                        <div><dt className={`text-xs ${tema.label}`}>Pago</dt><dd className="font-semibold">{formatCurrency(emenda.valorPago ?? 0)}</dd></div>
                      </dl>
                      {destino && <Link to={destino} className={`mt-3 inline-block text-sm font-semibold underline ${tema.link}`}>Ver a emenda {identificacao.numero}/{identificacao.exercicio}</Link>}
                    </li>
                  );
                })}
              </ul>

              <div className="hidden overflow-x-auto lg:block">
                <table className={`w-full divide-y text-sm ${tema.divider}`}>
                  <caption className="sr-only">Emendas parlamentares do resultado da pesquisa</caption>
                  <thead className={tema.tableHead}>
                    <tr>
                      {[['Emenda', 'left'], ['Parlamentar / beneficiário', 'left'], ['Objeto', 'left'], ['Previsto', 'right'], ['Pago', 'right'], ['Situação', 'left'], ['', 'left']].map(([column, align]) => (
                        <th key={column || 'acoes'} scope="col" className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide ${align === 'right' ? 'text-right' : 'text-left'}`}>{column || <span className="sr-only">Ações</span>}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${tema.divider}`}>
                    {emendas.map((emenda, index) => {
                      const key = emenda.id ?? `${emenda.numeroEmenda}-${index}`;
                      const aberta = emendaAberta === key;
                      const identificacao = emendaNumeroExercicio(emenda.numeroEmenda, emenda.exercicio, emenda.officialCode);
                      const destino = linkEmenda(emenda);
                      return (
                        <React.Fragment key={key}>
                          <tr className={tema.rowHover}>
                            <td className="px-4 py-3 align-top">
                              <p className="font-semibold">{identificacao.numero}/{identificacao.exercicio}</p>
                              <p className={`text-xs ${tema.muted}`}>{emenda.officialCode || 'Código não informado'}</p>
                              <p className={`mt-1 text-xs ${tema.muted}`}>{nomeTipo(emenda.classification)}</p>
                            </td>
                            <td className="px-4 py-3 align-top"><p className="font-medium">{emenda.councilorName || 'Autoria não informada'}</p><p className={`text-xs ${tema.muted}`}>{emenda.institutionName || 'Beneficiário não informado'}</p></td>
                            <td className="max-w-xs px-4 py-3 align-top"><p className="line-clamp-2">{emenda.objectDetail || 'Não informado'}</p><p className={`mt-1 text-xs ${tema.muted}`}>Função: {formatFuncaoGoverno(emenda.funcaoCodigo)}</p></td>
                            <td className="whitespace-nowrap px-4 py-3 text-right align-top font-medium">{formatCurrency(emenda.value)}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right align-top">
                              <p className="font-medium">{formatCurrency(emenda.valorPago ?? 0)}</p>
                              {formatPercent(emenda.valorPago, emenda.value) && <p className={`text-xs ${tema.muted}`}>{formatPercent(emenda.valorPago, emenda.value)}</p>}
                            </td>
                            <td className="px-4 py-3 align-top"><SituacaoBadge status={emenda.status} alto={tema.alto} /></td>
                            <td className="px-4 py-3 align-top">
                              <div className="flex flex-col items-start gap-2">
                                {destino && <Link to={destino} className={`whitespace-nowrap text-sm font-semibold underline ${tema.link}`}>Ver emenda<span className="sr-only"> {identificacao.numero}/{identificacao.exercicio}</span></Link>}
                                <button type="button" onClick={() => setEmendaAberta(aberta ? null : key)} className={`inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold ${tema.muted}`} aria-expanded={aberta} aria-label={`${aberta ? 'Recolher' : 'Mostrar'} resumo da emenda ${identificacao.numero}/${identificacao.exercicio}`}>
                                  {aberta ? 'Recolher' : 'Resumo'}{aberta ? <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {aberta && (
                            <tr className={tema.soft}>
                              <td colSpan={7} className="px-5 py-5">{detalheExpandido(emenda)}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <DashboardPagination
                page={pagina}
                pageSize={porPagina}
                totalItems={totalItems}
                onPageChange={value => { setPagina(value); syncUrl(filtrosAtivos, exercicioAtivo, value, porPagina); }}
                onPageSizeChange={value => { setPagina(1); setPorPagina(value); syncUrl(filtrosAtivos, exercicioAtivo, 1, value); }}
                pageSizeOptions={POR_PAGINA_OPTIONS}
                itemLabel="emendas"
              />
            </>
          )}
        </section>
      </main>
    </PortalAcessibilidadeShell>
  );
};

export default TransparenciaPublicaPage;
